/**
 * Team Spain v0 — Modal de Configuración Horaria de Formularios Semanales (M6)
 * 
 * Permite al staff autorizado (coach, admin, medic) configurar:
 * - Día y hora de apertura del cuestionario semanal (por defecto: Domingo 18:00)
 * - Día y hora límite recomendada de entrega (por defecto: Lunes 12:00)
 * - Zona horaria del equipo
 * 
 * Los cambios persisten en public.team_form_settings con RLS.
 */

import React, { useState, useEffect } from 'react'
import { Modal } from '../shared/Modal'
import { formsService } from './formsService'
import { Clock, Calendar, Globe, AlertCircle, CheckCircle2 } from 'lucide-react'

interface TeamScheduleModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  teamId: string
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
]

export const TeamScheduleModal: React.FC<TeamScheduleModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  teamId,
}) => {
  const [openDay, setOpenDay] = useState<number>(0) // Domingo
  const [openTime, setOpenTime] = useState<string>('18:00')
  const [dueDay, setDueDay] = useState<number>(1)   // Lunes
  const [dueTime, setDueTime] = useState<string>('12:00')
  const [dailyDueTime, setDailyDueTime] = useState<string>('09:00')
  const [timezone, setTimezone] = useState<string>('Europe/Madrid')

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) return
    setIsLoading(true)
    setErrorMsg(null)
    setSuccessMsg(null)

    formsService
      .getTeamFormSettings(teamId)
      .then((settings) => {
        if (settings) {
          setOpenDay(settings.weekly_open_day ?? 0)
          setOpenTime((settings.weekly_open_time || '18:00:00').slice(0, 5))
          setDueDay(settings.weekly_due_day ?? 1)
          setDueTime((settings.weekly_due_time || '12:00:00').slice(0, 5))
          setDailyDueTime((settings.daily_checkin_due_time || '09:00:00').slice(0, 5))
          setTimezone(settings.timezone || 'Europe/Madrid')
        }
      })
      .catch((err) => {
        console.error('Error al cargar configuración:', err)
        setErrorMsg('No se pudo cargar la configuración actual.')
      })
      .finally(() => setIsLoading(false))
  }, [isOpen, teamId])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    setErrorMsg(null)

    try {
      await formsService.updateTeamFormSettings(teamId, {
        weekly_open_day: openDay,
        weekly_open_time: `${openTime}:00`,
        weekly_due_day: dueDay,
        weekly_due_time: `${dueTime}:00`,
        daily_checkin_due_time: `${dailyDueTime}:00`,
        timezone: timezone.trim() || 'Europe/Madrid',
      })

      setSuccessMsg('Horario de formularios actualizado correctamente.')
      setTimeout(() => {
        onSuccess()
        onClose()
      }, 1000)
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err)
      setErrorMsg(`Error al guardar: ${msg}`)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Configurar Horario Semanal de Formularios"
      size="md"
    >
      {isLoading ? (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Cargando configuración del equipo...
        </div>
      ) : (
        <form onSubmit={handleSave}>
          <p style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', marginBottom: '1.25rem', lineHeight: 1.5 }}>
            Define cuándo se abre el cuestionario de seguimiento semanal para los deportistas y el plazo límite recomendado de entrega. Los envíos posteriores a la fecha límite se registrarán con la marca <em>«Fuera de plazo»</em> vinculados al periodo original.
          </p>

          {errorMsg && (
            <div
              style={{
                padding: '0.75rem',
                backgroundColor: 'var(--status-danger-bg)',
                color: 'var(--status-danger-text)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: '1rem',
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <AlertCircle size={16} />
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div
              style={{
                padding: '0.75rem',
                backgroundColor: 'var(--status-success-bg)',
                color: 'var(--status-success-text)',
                borderRadius: 'var(--radius-sm)',
                marginBottom: '1rem',
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <CheckCircle2 size={16} />
              <span>{successMsg}</span>
            </div>
          )}

          {/* Apertura semanal */}
          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--surface-sunken)',
              borderRadius: 'var(--radius-sm)',
              marginBottom: '1rem',
              border: '1px solid var(--surface-border)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Clock size={16} style={{ color: 'var(--brand-yellow)' }} />
              <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>Apertura Semanal</h4>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: '0.3rem' }}>
                  Día de apertura
                </label>
                <select
                  className="input-field"
                  value={openDay}
                  onChange={(e) => setOpenDay(Number(e.target.value))}
                  style={{ width: '100%', fontSize: '0.84rem' }}
                >
                  {DAYS_OF_WEEK.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: '0.3rem' }}>
                  Hora de apertura
                </label>
                <input
                  type="time"
                  className="input-field"
                  value={openTime}
                  onChange={(e) => setOpenTime(e.target.value)}
                  required
                  style={{ width: '100%', fontSize: '0.84rem' }}
                />
              </div>
            </div>
          </div>

          {/* Entrega recomendada */}
          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--surface-sunken)',
              borderRadius: 'var(--radius-sm)',
              marginBottom: '1rem',
              border: '1px solid var(--surface-border)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Calendar size={16} style={{ color: 'var(--brand-red)' }} />
              <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>Límite Recomendado de Entrega</h4>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: '0.3rem' }}>
                  Día de entrega
                </label>
                <select
                  className="input-field"
                  value={dueDay}
                  onChange={(e) => setDueDay(Number(e.target.value))}
                  style={{ width: '100%', fontSize: '0.84rem' }}
                >
                  {DAYS_OF_WEEK.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: '0.3rem' }}>
                  Hora límite
                </label>
                <input
                  type="time"
                  className="input-field"
                  value={dueTime}
                  onChange={(e) => setDueTime(e.target.value)}
                  required
                  style={{ width: '100%', fontSize: '0.84rem' }}
                />
              </div>
            </div>
          </div>

          {/* Check-in Diario de Concentración (M8D) */}
          <div
            style={{
              padding: '1rem',
              backgroundColor: 'var(--surface-sunken)',
              borderRadius: 'var(--radius-sm)',
              marginBottom: '1rem',
              border: '1px solid var(--surface-border)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <Clock size={16} style={{ color: 'var(--brand-yellow)' }} />
              <h4 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>Concentración: Check-in Diario</h4>
            </div>
            <p style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginBottom: '0.75rem', lineHeight: 1.4 }}>
              Hora límite para el envío del cuestionario matinal durante las jornadas de concentración. La apertura se produce a las 00:00 del día local.
            </p>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'block', marginBottom: '0.3rem' }}>
                Hora límite diaria (inicial: 09:00)
              </label>
              <input
                type="time"
                className="input-field"
                value={dailyDueTime}
                onChange={(e) => setDailyDueTime(e.target.value)}
                required
                style={{ width: '100%', maxWidth: '200px', fontSize: '0.84rem' }}
              />
            </div>
          </div>

          {/* Zona horaria */}
          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label style={{ fontSize: '0.78rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.3rem' }}>
              <Globe size={14} />
              <span>Zona horaria de referencia</span>
            </label>
            <input
              type="text"
              className="input-field"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="Europe/Madrid"
              required
              style={{ width: '100%', fontSize: '0.84rem' }}
            />
            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.2rem', display: 'block' }}>
              Utilizada para evaluar con precisión las aperturas y vencimientos en horario peninsular o insular.
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSaving}
              style={{ fontSize: '0.84rem' }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving}
              style={{ fontSize: '0.84rem' }}
            >
              {isSaving ? 'Guardando...' : 'Guardar Horario'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}
